import React, { useMemo } from 'react';
import {
  Tabs,
  TabsItem,
  useApi,
  useOnboardingText,
  Card,
  CardSection,
  Paragraph,
  Layout,
  LayoutItem,
} from '@axiom/ui';
import type { Candidate, Opportunity } from '@axiom/validation';

import { EnvUtil } from '../../utils/env-util';
import { OpportunityApi } from '../../api/opportunity-api';
import NoResults from '../../public/svg/no-results-binoculars.svg';
import { TaxonomyApi } from '../../api/taxonomy-api';

import { OnboardingDetails } from './OnboardingDetails';
import { OnboardingUtil } from './onboarding-util';

export type OnboardingProps = {
  name: string;
  opportunity: Opportunity;
};

export const Onboarding = ({ name, opportunity }: OnboardingProps) => {
  const [{ data: onboarding }, { data: taxonomy }] = useApi(
    OpportunityApi.readOpportunitiesOnboarding({
      opportunityId: opportunity.id,
    }),
    TaxonomyApi.readTaxonomy()
  );

  const { getOnboardingTexts, formatEngagementAddress } = useOnboardingText({
    data: onboarding,
    urls: {
      onboardingTimeEntryUrl: EnvUtil.onboardingTimeEntryUrl,
      onboardingLinkedinGuideUrl: EnvUtil.onboardingLinkedinGuideUrl,
      onboardingAodaInstructionsUrl: EnvUtil.onboardingAodaInstructionsUrl,
      onboardingLsoLawproUrl: EnvUtil.onboardingLsoLawproUrl,
      onboardingLawSocietyGuideUrl: EnvUtil.onboardingLawSocietyGuideUrl,
      onboardingEngagementSuccessUrl: EnvUtil.onboardingEngagementSuccessUrl,
      onboardingEncryptedEmailUrl: EnvUtil.onboardingEncryptedEmailUrl,
      workdayHelpcenterUrl: null,
    },
    taxonomy,
  });

  const handleOnSave = async ({
    candidateId,
    callbackFn,
  }: {
    candidateId: Candidate['id'];
    callbackFn: () => void;
  }) => {
    await OpportunityApi.createSendOnboardingEmail({
      opportunityId: opportunity.id,
      candidateId,
    });
    callbackFn();
    await OpportunityApi.refreshOpportunitiesOnboarding({
      opportunityId: opportunity.id,
    });
  };

  const startTab = useMemo(() => {
    const firstUnsent = onboarding.positions?.find(
      pos => !OnboardingUtil.getEmailSentDate(pos)
    );
    return firstUnsent ? `POSITION_${firstUnsent.id}` : undefined;
  }, [onboarding.positions]);

  return Array.isArray(onboarding.positions) &&
    onboarding.positions.length > 0 ? (
    <Tabs name={name} startTab={startTab}>
      {onboarding.positions.map((pos, idx) => {
        const texts = getOnboardingTexts(pos);
        return (
          <TabsItem
            icon={
              OnboardingUtil.getEmailSentDate(pos)
                ? 'circle-check-filled'
                : undefined
            }
            key={pos.id}
            label={`Position ${idx + 1}`}
            name={`POSITION_${pos.id}`}
          >
            <OnboardingDetails
              name={`DETAILS_TAB_${pos.id}`}
              position={pos}
              opportunityId={opportunity.id}
              formatEngagementAddress={formatEngagementAddress}
              texts={texts}
              onSave={handleOnSave}
            />
          </TabsItem>
        );
      })}
    </Tabs>
  ) : (
    <Card name={`${name}_NO_POSITIONS`}>
      <CardSection>
        <Layout position="center middle">
          <LayoutItem rightGutter="24px">
            <NoResults width="117px" height="117px" />
          </LayoutItem>
          <LayoutItem>
            <Paragraph>
              There are no talent that need onboarding for this opportunity.
            </Paragraph>
          </LayoutItem>
        </Layout>
      </CardSection>
    </Card>
  );
};
